<template>
  <ManagerLayout>
    <div class="manager-submissions">
      <h2 class="page-title">提出管理</h2>
      <p class="page-desc">課題・練習の提出一覧と、講師の返信データを一覧で確認できます。</p>

      <!-- Day0ミニ体験の提出 -->
      <section class="manager-section">
        <div class="card">
          <div class="toolbar">
            <h3 class="section-title">Day0ミニ体験の提出</h3>
            <select v-model="day0StatusFilter" class="form-select" @change="loadDay0Submissions">
              <option value="submitted">未対応</option>
              <option value="feedback_sent">FB送信済み</option>
              <option value="no_reply_needed">返信不要</option>
              <option value="all">すべて</option>
            </select>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="day0Loading" @click="loadDay0Submissions">
              更新
            </button>
          </div>
          <div v-if="day0Loading" class="loading-state">読み込み中...</div>
          <div v-else-if="!day0Submissions.length" class="empty-state">該当するDay0提出がありません</div>
          <div v-else class="table-wrap submissions-table-wrap">
            <table class="data-table" aria-label="Day0提出一覧">
              <thead>
                <tr>
                  <th>提出日時（JST）</th>
                  <th>お名前</th>
                  <th>属性</th>
                  <th class="num">回数</th>
                  <th>提出音声</th>
                  <th>ステータス</th>
                  <th>返信音声</th>
                  <th class="action">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in day0Submissions" :key="d.id" class="data-row">
                  <td>{{ formatDateTimeJst(d.submittedAt) }}</td>
                  <td>
                    {{ day0NameLabel(d) }}
                    <span v-if="day0KindLabel(d)" class="day0-voice-badge">{{ day0KindLabel(d) }}</span>
                  </td>
                  <td>{{ d.audience === 'child' ? 'お子さま' : '大人' }}</td>
                  <td class="num">
                    {{ d.attemptNumber }}
                    <span v-if="day0DurationLabel(d)" class="day0-duration">{{ day0DurationLabel(d) }}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      class="btn btn-sm btn-play"
                      :disabled="day0PlayingId === d.id && day0PlayingKind === 'submission'"
                      @click="playDay0Audio(d, 'submission')"
                    >
                      {{ day0PlayingId === d.id && day0PlayingKind === 'submission' ? '再生中...' : '▶ 提出' }}
                    </button>
                  </td>
                  <td>
                    <span class="status-badge" :class="d.status">{{ day0StatusText(d.status) }}</span>
                  </td>
                  <td>
                    <button
                      v-if="d.feedbackUrl"
                      type="button"
                      class="btn btn-sm btn-play"
                      :disabled="day0PlayingId === d.id && day0PlayingKind === 'feedback'"
                      @click="playDay0Audio(d, 'feedback')"
                    >
                      {{ day0PlayingId === d.id && day0PlayingKind === 'feedback' ? '再生中...' : '▶ 返信' }}
                    </button>
                    <span v-else class="no-audio">—</span>
                  </td>
                  <td class="action">
                    <div v-if="d.status === 'submitted'" class="action-buttons">
                      <button type="button" class="btn btn-sm btn-reply" @click="openDay0FeedbackModal(d)">
                        フィードバック録音
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-no-reply"
                        :disabled="day0NoReplyProcessingId === d.id"
                        @click="markDay0NoReply(d)"
                      >
                        {{ day0NoReplyProcessingId === d.id ? '処理中...' : '🚫 返信不要' }}
                      </button>
                    </div>
                    <span v-else class="no-action">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <audio
            ref="day0AudioEl"
            controls
            class="day0-inline-audio"
            @ended="onDay0AudioEnded"
            @pause="onDay0AudioEnded"
            @error="onDay0AudioEnded"
          />
        </div>
      </section>

      <!-- Day0フィードバック録音モーダル -->
      <div v-if="showDay0Modal" class="day0-modal-overlay" @click.self="closeDay0Modal">
        <div class="day0-modal-content">
          <h3>{{ day0FeedbackTitle(day0ModalTarget) }}</h3>
          <audio :src="day0ModalTarget?.recordingUrl" controls class="day0-modal-audio"></audio>

          <div v-if="!day0IsRecording && !day0RecordedBlob" class="day0-mode-toggle">
            <label>
              <input type="radio" value="audio" v-model="day0FeedbackMode" /> 音声
            </label>
            <label v-if="isVideoSupported">
              <input type="radio" value="video" v-model="day0FeedbackMode" /> 動画
            </label>
          </div>

          <p v-if="day0MicError" class="day0-modal-error">{{ day0MicError }}</p>

          <video
            v-if="!day0RecordedBlob && day0FeedbackMode === 'video'"
            ref="day0LivePreviewEl"
            muted
            autoplay
            playsinline
            class="day0-modal-video day0-modal-video-live"
          ></video>
          <p v-if="!day0RecordedBlob && day0FeedbackMode === 'audio' && !day0PreviewReady" class="day0-preview-note">
            マイクを準備しています...
          </p>
          <p v-if="day0IsRecording" class="day0-recording-timer">🔴 {{ formatDay0RecordingTime(day0RecordingSeconds) }}</p>

          <div class="day0-modal-actions">
            <button
              v-if="!day0IsRecording && !day0RecordedBlob"
              type="button"
              class="btn btn-primary"
              :disabled="!day0PreviewReady"
              @click="startDay0Feedback"
            >
              ●録音開始
            </button>
            <button
              v-else-if="day0IsRecording"
              type="button"
              class="btn btn-danger"
              @click="stopDay0Feedback"
            >
              ■停止
            </button>
            <template v-else>
              <video v-if="day0FeedbackMode === 'video'" :src="day0RecordedPreviewUrl" controls playsinline class="day0-modal-video"></video>
              <audio v-else :src="day0RecordedPreviewUrl" controls class="day0-modal-audio"></audio>
              <button type="button" class="btn btn-secondary" :disabled="day0Uploading" @click="resetDay0Recording">
                録り直す
              </button>
              <button type="button" class="btn btn-primary" :disabled="day0Uploading" @click="submitDay0Feedback">
                {{ day0Uploading ? '送信中...' : 'このままLINEへ送信' }}
              </button>
            </template>
          </div>
          <button type="button" class="btn btn-secondary day0-modal-close" @click="closeDay0Modal">閉じる</button>
        </div>
      </div>

      <!-- フィルタ -->
      <section class="manager-section filters-section">
        <div class="card">
          <h3 class="section-title">フィルタ</h3>
          <div class="filters">
            <label class="filter-item">
              <span class="filter-label">種別</span>
              <select v-model="filterType" class="form-select" @change="loadSubmissions">
                <option value="all">すべて</option>
                <option value="assignment">課題</option>
                <option value="practice">練習</option>
              </select>
            </label>
            <label class="filter-item">
              <span class="filter-label">ステータス</span>
              <select v-model="filterStatus" class="form-select" @change="loadSubmissions">
                <option value="all">すべて</option>
                <option value="submitted">提出済み</option>
                <option value="feedback_pending">FB待ち</option>
                <option value="feedback_completed">FB完了</option>
                <option value="no_reply_needed">返信不要</option>
              </select>
            </label>
            <label class="filter-item">
              <span class="filter-label">日付（から）</span>
              <input v-model="filterDateFrom" type="date" class="form-input" @change="loadSubmissions" />
            </label>
            <label class="filter-item">
              <span class="filter-label">日付（まで）</span>
              <input v-model="filterDateTo" type="date" class="form-input" @change="loadSubmissions" />
            </label>
            <button type="button" class="btn btn-primary" :disabled="loading" @click="loadSubmissions">
              {{ loading ? '読み込み中...' : '検索' }}
            </button>
          </div>
        </div>
      </section>

      <!-- 一覧 -->
      <section class="manager-section">
        <div class="card">
          <div class="toolbar">
            <span class="total-note">全 {{ total }} 件</span>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="loading" @click="loadSubmissions">
              更新
            </button>
            <button type="button" class="btn btn-primary btn-sm" :disabled="exportCsvLoading" @click="downloadSubmissionsCsv">
              {{ exportCsvLoading ? '取得中...' : 'CSVダウンロード' }}
            </button>
          </div>
          <div v-if="loading" class="loading-state">読み込み中...</div>
          <div v-else-if="!submissions.length" class="empty-state">該当する提出がありません</div>
          <div v-else class="table-wrap submissions-table-wrap">
            <table class="data-table" aria-label="提出一覧">
              <thead>
                <tr>
                  <th>提出日時（JST）</th>
                  <th>生徒名</th>
                  <th>ふりがな</th>
                  <th>学年</th>
                  <th class="num">Day</th>
                  <th>種別</th>
                  <th>ステータス</th>
                  <th>提出音声</th>
                  <th>返信講師</th>
                  <th>返信日時（JST）</th>
                  <th>返信音声</th>
                  <th class="action">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in submissions" :key="s.id" class="data-row">
                  <td>{{ formatDateTimeJst(s.createdAt) }}</td>
                  <td>{{ s.studentName }}</td>
                  <td>{{ s.studentNameKana || '—' }}</td>
                  <td>{{ s.studentGrade || '—' }}</td>
                  <td class="num">{{ s.day }}</td>
                  <td>
                    <span class="type-badge" :class="s.type">{{ s.type === 'practice' ? '練習' : '課題' }}</span>
                  </td>
                  <td>
                    <span class="status-badge" :class="s.status">{{ getStatusText(s.status) }}</span>
                  </td>
                  <td>
                    <button
                      v-if="s.hasSubmissionAudio"
                      type="button"
                      class="btn btn-sm btn-play"
                      :disabled="playingSubmissionAudioId === s.id"
                      @click="playSubmissionAudio(s.id)"
                    >
                      {{ playingSubmissionAudioId === s.id ? '再生中...' : '▶ 提出' }}
                    </button>
                    <span v-else class="no-audio">—</span>
                  </td>
                  <td>{{ s.feedbackInstructorName || '—' }}</td>
                  <td>{{ s.feedbackSubmittedAt ? formatDateTimeJst(s.feedbackSubmittedAt) : '—' }}</td>
                  <td>
                    <button
                      v-if="s.hasFeedbackAudio"
                      type="button"
                      class="btn btn-sm btn-play"
                      :disabled="playingId === s.id"
                      @click="playFeedback(s.id)"
                    >
                      {{ playingId === s.id ? '再生中...' : '▶ 返信' }}
                    </button>
                    <span v-else class="no-audio">—</span>
                  </td>
                  <td class="action">
                    <div v-if="canReply(s) || canDeleteFeedback(s) || canMarkNoReply(s) || canUnmarkNoReply(s)" class="action-buttons">
                      <button
                        v-if="canReply(s)"
                        type="button"
                        class="btn btn-sm btn-reply"
                        :disabled="isProcessingLock || deletingSubmissionId === s.id"
                        @click="handleReply(s)"
                      >
                        {{ isProcessingLock ? '処理中...' : '返信' }}
                      </button>
                      <button
                        v-if="canDeleteFeedback(s)"
                        type="button"
                        class="btn btn-sm btn-delete"
                        :disabled="isProcessingLock || deletingSubmissionId === s.id"
                        @click="handleDeleteFeedback(s)"
                      >
                        {{ deletingSubmissionId === s.id ? '削除中...' : 'FB削除' }}
                      </button>
                      <button
                        v-if="canMarkNoReply(s)"
                        type="button"
                        class="btn btn-sm btn-no-reply"
                        :disabled="noReplyProcessingId === s.id"
                        @click="handleMarkNoReply(s)"
                      >
                        {{ noReplyProcessingId === s.id ? '処理中...' : '🚫 返信不要' }}
                      </button>
                      <button
                        v-if="canUnmarkNoReply(s)"
                        type="button"
                        class="btn btn-sm btn-unmark-no-reply"
                        :disabled="noReplyProcessingId === s.id"
                        @click="handleUnmarkNoReply(s)"
                      >
                        {{ noReplyProcessingId === s.id ? '処理中...' : '🔄 返信不要解除' }}
                      </button>
                    </div>
                    <span v-else class="no-action">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="submissions.length > 0 && submissions.length < total" class="load-more">
            <button type="button" class="btn btn-secondary" :disabled="loadingMore" @click="loadMore">
              {{ loadingMore ? '読み込み中...' : 'さらに読み込む' }}
            </button>
          </div>
          <audio
            ref="audioEl"
            controls
            class="feedback-audio-player"
            @ended="onAudioEnded"
            @pause="onAudioPause"
            @error="onAudioError"
          />
          <video
            v-show="playingMediaKind === 'video'"
            ref="feedbackVideoEl"
            controls
            playsinline
            class="feedback-video-review-player"
            @ended="onAudioEnded"
            @pause="onAudioPause"
            @error="onAudioError"
          />
        </div>
      </section>

      <!-- 管理者用・全画面フィードバックモーダル（講師 home の「この子に応える！」と同じ挙動） -->
      <Transition name="fullscreen-modal">
        <div v-if="showFeedbackModal" class="fullscreen-feedback-modal" @click.self="canCloseModal ? closeFeedbackModal() : null">
          <div class="fullscreen-content">
            <button @click="closeFeedbackModal" class="close-btn" :disabled="!canCloseModal" :aria-disabled="!canCloseModal" :tabindex="!canCloseModal ? -1 : 0" :title="canCloseModal ? '閉じる' : '操作中のため閉じられません'">
              {{ canCloseModal ? '✕' : '⏳' }}
            </button>
            <div class="lock-timer-bar" :class="lockTimerClass">
              <span class="lock-timer-label">⏱️ 残り時間:</span>
              <span class="lock-timer-value">{{ remainingTimeDisplay }}</span>
              <span v-if="showExpiryWarning" class="lock-timer-warning">まもなく期限切れです</span>
            </div>
            <div class="feedback-screen">
              <div class="feedback-header-mobile">
                <div class="student-name-line">{{ currentFeedbackSubmission?.studentName || '未設定' }}</div>
                <div class="assignment-line">Day {{ currentFeedbackSubmission?.day }} {{ currentFeedbackSubmission?.type === 'practice' ? '練習' : '課題' }}</div>
              </div>
              <div class="feedback-sections">
                <div class="feedback-section feedback-manual-section">
                  <h3 class="manual-title">📖 フィードバックの手順</h3>
                  <div class="manual-content">
                    <div class="manual-steps">
                      <p>1.（下の名前）さんへ、<br>Day△△お疲れさまでした！</p>
                      <p>2.今日の良かったところは・・・。<br>（最初に良かった点を1つ以上伝える）</p>
                      <p>3.今日の復習は、〇〇の部分です。<br>△△を意識して3回、読んでみましょう！</p>
                      <p>4.（あれば一言加えて、）明日の提出も楽しみにしています！</p>
                    </div>
                  </div>
                </div>
                <div class="feedback-section feedback-assignment-section">
                  <h3>📋 Day {{ currentFeedbackSubmission?.day }} 課題内容</h3>
                  <div class="assignment-content">
                    <p><strong>課題タイトル:</strong> Day {{ currentFeedbackSubmission?.day }} の発音練習</p>
                    <p><strong>練習内容:</strong> 指定された英単語・フレーズの発音練習</p>
                    <p><strong>評価ポイント:</strong> 発音の正確性、イントネーション、流暢性</p>
                  </div>
                </div>
                <div class="feedback-controls-section">
                  <div class="feedback-section feedback-audio-section">
                    <h4 class="submitted-audio-title">提出音声</h4>
                    <div class="submitted-audio-section">
                      <div class="audio-slot">
                        <div v-if="submissionAudioLoading" class="audio-slot-label">読み込み中...</div>
                        <div v-else-if="submissionAudioUrl" class="audio-player-section">
                          <audio :src="submissionAudioUrl" controls preload="metadata" playsinline class="fullscreen-audio-player" controlslist="nodownload noplaybackrate" />
                        </div>
                        <div v-else class="audio-slot-label">—</div>
                      </div>
                    </div>
                  </div>
                  <div class="feedback-section feedback-recording-section">
                    <div class="feedback-recording-section">
                      <div v-if="canShowStartButton && !isPreviewReady" class="recording-start">
                        <div class="media-kind-toggle">
                          <label>
                            <input type="radio" value="audio" v-model="selectedMediaKind" />
                            🎙️ 音声
                          </label>
                          <label :class="{ 'is-disabled': !isVideoSupported }">
                            <input type="radio" value="video" v-model="selectedMediaKind" :disabled="!isVideoSupported" />
                            🎬 動画
                          </label>
                          <p v-if="!isVideoSupported" class="media-kind-note">この端末では動画フィードバックに対応していません（Android/PCのみ対応）</p>
                        </div>
                        <button
                          v-if="selectedMediaKind === 'video'"
                          @click="onPrepareVideoPreview"
                          class="btn btn-primary large"
                          :disabled="!canStartNow || isPreparingPreview"
                        >{{ isPreparingPreview ? '📷 カメラ準備中...' : '🎬 カメラ準備' }}</button>
                        <button v-else @click="startRecording" class="btn btn-primary large" :disabled="!canStartNow">🎙️ 録音開始</button>
                      </div>
                      <div v-if="isPreviewReady && !isRecording" class="video-preview-section">
                        <div class="preview-outer">
                          <video
                            :ref="setPreviewVideoEl"
                            class="recording-preview"
                            :style="previewVideoStyle"
                            autoplay
                            muted
                            playsinline
                          ></video>
                        </div>
                        <button @click="onRotatePreview" class="btn btn-secondary rotate-btn">↻ 向きを直す</button>
                        <div class="preview-actions">
                          <button @click="onStartVideoRecording" class="btn btn-primary large">🎬 録画開始</button>
                          <button @click="onCancelVideoPreview" class="btn btn-secondary">キャンセル</button>
                        </div>
                      </div>
                      <div v-if="isRecording" class="recording-active">
                        <div v-if="selectedMediaKind === 'video'" class="preview-outer">
                          <video
                            :ref="setPreviewVideoEl"
                            class="recording-preview"
                            :style="previewVideoStyle"
                            autoplay
                            muted
                            playsinline
                          ></video>
                        </div>
                        <div class="recording-indicator">
                          <span class="recording-dot"></span>
                          <span>録音中...</span>
                        </div>
                        <div class="recording-time">{{ formatRecordingTime(recordingTime) }}</div>
                        <button @click="stopRecording" class="btn btn-danger" :disabled="!canStopRecording" :class="{ 'btn-disabled': !canStopRecording }">
                          {{ canStopRecording ? '⏹️ 録音停止' : `⏹️ 録音停止 (${Math.max(0, 3 - recordingTime)}秒後)` }}
                        </button>
                      </div>
                      <div v-if="isUploading" class="upload-progress-section">
                        <div class="upload-indicator">
                          <span class="upload-spinner">📤</span>
                          <span>アップロード中...</span>
                        </div>
                        <div class="progress-bar">
                          <div class="progress-fill" :style="{ width: uploadProgress.percentage + '%' }"></div>
                        </div>
                        <div class="progress-text">{{ uploadProgress.completed }} / {{ uploadProgress.total }} チャンク</div>
                      </div>
                      <div v-if="isCompleted && !isRecording && !isUploading" class="recording-completed">
                        <div class="completed-message">
                          <span class="success-icon">✅</span>
                          <span>録音が完了しました</span>
                        </div>
                        <div v-if="finalAudioUrl" class="audio-player-section">
                          <video v-if="selectedMediaKind === 'video'" :src="finalAudioUrl" controls playsinline preload="metadata" class="feedback-video-player" controlslist="nodownload noplaybackrate" />
                          <audio v-else :src="finalAudioUrl" controls preload="metadata" class="feedback-audio-player" controlslist="nodownload noplaybackrate" playsinline />
                        </div>
                        <div class="recording-actions">
                          <button @click="retakeRecording" class="btn btn-secondary" :disabled="isDeleting">{{ isDeleting ? '🗑️ 削除中...' : '🔄 撮り直し' }}</button>
                          <button @click="handleSendFeedback" class="btn btn-success" :disabled="isDeleting || isSubmitting">{{ isSubmitting ? '送信中...' : '📤 送信' }}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </ManagerLayout>
</template>

<script setup>
import { day0NameLabel, day0KindLabel, day0DurationLabel, day0FeedbackTitle } from '../../utils/day0SubmissionView.js'
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import ManagerLayout from '../../components/layout/ManagerLayout.vue'
import { supabase } from '../../supabaseClient'
import { formatRemainingTime, acceptTaskWithLock, releaseFeedbackLock } from '../instructor/instructorFeedbackHelpers.js'
import { useFeedbackRecorder } from '../../composables/useFeedbackRecorder.js'
import { isVideoFeedbackSupported } from '../../utils/videoRecorder.js'
import { createDay0ChunkRecorder } from '../../composables/useDay0ChunkedRecorder.js'

const apiBase = import.meta.env.VITE_API_BASE_URL || ''
const PAGE_SIZE = 200

// ===== Day0ミニ体験の提出 =====
const day0Submissions = ref([])
const day0Loading = ref(false)
const day0StatusFilter = ref('submitted')
const day0NoReplyProcessingId = ref(null)

const showDay0Modal = ref(false)
const day0ModalTarget = ref(null)
const day0IsRecording = ref(false)
const day0Uploading = ref(false)
const day0MicError = ref('')
const day0RecordedBlob = ref(null)
const day0RecordedPreviewUrl = ref(null)
const day0FeedbackMode = ref('audio') // 'audio' | 'video'
const day0LivePreviewEl = ref(null)
const day0PreviewReady = ref(false)
const day0RecordingSeconds = ref(0)
let day0RecordingTimer = null
let day0RecordingId = null
let day0RecordingCodecType = null

// 本番の学習者提出・フィードバック録音と同じ「数秒ごとのチャンク分割→逐次アップロード」方式（詳しくはcomposable参照）
// codecTypeも本番と同様に送信する（動画は常にwebm。音声はiOS/SafariだとWebM非対応でmp4になる）。
const day0FeedbackRecorder = createDay0ChunkRecorder({
  onStreamReady: (stream) => {
    if (day0LivePreviewEl.value) day0LivePreviewEl.value.srcObject = stream
  },
  uploadChunk: async (chunkData, chunkInfo) => {
    const formData = new FormData()
    const ext = chunkInfo.codecType === 'mp4' ? 'mp4' : 'webm'
    formData.append('chunk', chunkData, `chunk_${chunkInfo.chunkIndex}.${ext}`)
    formData.append('recordingId', chunkInfo.recordingId)
    formData.append('chunkIndex', String(chunkInfo.chunkIndex))
    formData.append('codecType', chunkInfo.codecType || 'webm')
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/day0-submissions/${day0ModalTarget.value.id}/feedback-chunk`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || !json.success) throw new Error(json?.error || 'チャンクの送信に失敗しました')
  }
})

// 一覧の「▶ 提出」「▶ 返信」再生（ページ読み込み時に全件を読み込まず、押した時だけ再生する）
const day0AudioEl = ref(null)
const day0PlayingId = ref(null)
const day0PlayingKind = ref(null) // 'submission' | 'feedback'

function playDay0Audio(d, kind) {
  if (!day0AudioEl.value) return
  const url = kind === 'feedback' ? d.feedbackUrl : d.recordingUrl
  if (!url) return
  day0PlayingId.value = d.id
  day0PlayingKind.value = kind
  day0AudioEl.value.src = url
  day0AudioEl.value.play().catch(() => {
    day0PlayingId.value = null
    day0PlayingKind.value = null
  })
}

function onDay0AudioEnded() {
  day0PlayingId.value = null
  day0PlayingKind.value = null
}

function day0StatusText(status) {
  const map = { submitted: '未対応', feedback_sent: 'FB送信済み', no_reply_needed: '返信不要' }
  return map[status] || status
}

async function loadDay0Submissions() {
  day0Loading.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/day0-submissions?status=${encodeURIComponent(day0StatusFilter.value)}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.success) throw new Error(data.error || 'Day0提出の取得に失敗しました')
    day0Submissions.value = data.data || []
  } catch (e) {
    console.error('Day0提出取得エラー:', e)
  } finally {
    day0Loading.value = false
  }
}

async function markDay0NoReply(d) {
  if (day0NoReplyProcessingId.value) return
  day0NoReplyProcessingId.value = d.id
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/day0-submissions/${d.id}/no-reply`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.success) throw new Error(data.error || '更新に失敗しました')
    d.status = 'no_reply_needed'
  } catch (e) {
    alert(e.message || '更新に失敗しました')
  } finally {
    day0NoReplyProcessingId.value = null
  }
}

async function openDay0FeedbackModal(d) {
  day0ModalTarget.value = d
  day0MicError.value = ''
  day0RecordedBlob.value = null
  day0RecordedPreviewUrl.value = null
  day0RecordingId = null
  day0RecordingCodecType = null
  day0FeedbackMode.value = isVideoSupported ? 'video' : 'audio'
  showDay0Modal.value = true
  await prepareDay0Preview()
}

function closeDay0Modal() {
  if (day0IsRecording.value) return
  stopDay0RecordingTimer()
  day0FeedbackRecorder.cancelPreview()
  day0PreviewReady.value = false
  showDay0Modal.value = false
  day0ModalTarget.value = null
}

/** モーダルを開いた直後・モード切替時に呼ぶ。カメラ/マイクを先に確保し、録音開始前にプレビュー（自撮り映像）を出す */
async function prepareDay0Preview() {
  day0MicError.value = ''
  day0PreviewReady.value = false
  const isVideo = day0FeedbackMode.value === 'video'
  try {
    if (isVideo) {
      await day0FeedbackRecorder.prepareVideoPreview()
    } else {
      await day0FeedbackRecorder.prepareAudioPreview()
    }
  } catch (e) {
    day0MicError.value = isVideo
      ? 'カメラ/マイクへのアクセスが許可されませんでした。'
      : 'マイクへのアクセスが許可されませんでした。'
    return
  }
  day0PreviewReady.value = true
}

watch(day0FeedbackMode, () => {
  if (showDay0Modal.value && !day0IsRecording.value && !day0RecordedBlob.value) {
    prepareDay0Preview()
  }
})

function startDay0RecordingTimer() {
  day0RecordingSeconds.value = 0
  day0RecordingTimer = setInterval(() => {
    day0RecordingSeconds.value += 1
  }, 1000)
}

function stopDay0RecordingTimer() {
  if (day0RecordingTimer) {
    clearInterval(day0RecordingTimer)
    day0RecordingTimer = null
  }
}

function formatDay0RecordingTime(sec) {
  const m = String(Math.floor(sec / 60)).padStart(2, '0')
  const s = String(sec % 60).padStart(2, '0')
  return `${m}:${s}`
}

async function startDay0Feedback() {
  if (!day0PreviewReady.value) {
    day0MicError.value = 'カメラ/マイクの準備ができていません。少し待ってからもう一度お試しください。'
    return
  }
  try {
    await day0FeedbackRecorder.start(day0FeedbackMode.value)
  } catch (e) {
    day0MicError.value = '録音を開始できませんでした。'
    return
  }
  day0IsRecording.value = true
  startDay0RecordingTimer()
}

async function stopDay0Feedback() {
  day0IsRecording.value = false
  stopDay0RecordingTimer()
  day0PreviewReady.value = false

  try {
    const { recordingId, previewBlob, codecType } = await day0FeedbackRecorder.stop()
    day0RecordingId = recordingId
    day0RecordingCodecType = codecType
    if (previewBlob) {
      day0RecordedBlob.value = previewBlob
      day0RecordedPreviewUrl.value = URL.createObjectURL(previewBlob)
    }
  } catch (e) {
    day0MicError.value = '録音データの送信中にエラーが発生しました。もう一度お試しください。'
  }
}

function resetDay0Recording() {
  day0RecordedBlob.value = null
  if (day0RecordedPreviewUrl.value) URL.revokeObjectURL(day0RecordedPreviewUrl.value)
  day0RecordedPreviewUrl.value = null
  day0RecordingId = null
  day0RecordingCodecType = null
  prepareDay0Preview()
}

/** チャンクは録音中に既にアップロード済みなので、ここではサーバー側の結合・mp4変換・LINE送信を依頼するだけでよい */
async function submitDay0Feedback() {
  if (!day0RecordingId || !day0ModalTarget.value) return
  day0Uploading.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/day0-submissions/${day0ModalTarget.value.id}/feedback-finalize`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ recordingId: day0RecordingId, mediaKind: day0FeedbackMode.value, codecType: day0RecordingCodecType })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.success) throw new Error(data.error || '送信に失敗しました')

    const target = day0Submissions.value.find((x) => x.id === day0ModalTarget.value.id)
    if (target) target.status = 'feedback_sent'
    closeDay0Modal()
  } catch (e) {
    day0MicError.value = e.message || '送信に失敗しました'
  } finally {
    day0Uploading.value = false
  }
}

const filterType = ref('all')
const filterStatus = ref('all')
const filterDateFrom = ref('')
const filterDateTo = ref('')
const submissions = ref([])
const total = ref(0)
const loading = ref(false)
const loadingMore = ref(false)
const playingId = ref(null)
const playingSubmissionAudioId = ref(null)
const deletingSubmissionId = ref(null)
const noReplyProcessingId = ref(null)
const audioEl = ref(null)
const feedbackVideoEl = ref(null)
const playingMediaKind = ref('audio') // 「▶ 返信」再生中のメディア種別（audio/video）
const currentOffset = ref(0)
const exportCsvLoading = ref(false)

// 管理者用返信モーダル・ロック
const showFeedbackModal = ref(false)
const currentFeedbackSubmission = ref(null)
const isProcessingLock = ref(false)
const lockExpiresAt = ref(null)
const lockTimerInterval = ref(null)
const remainingTimeDisplay = ref('--:--')
const remainingSeconds = ref(0)
const submissionAudioUrl = ref(null)
const submissionAudioLoading = ref(false)
const isSubmitting = ref(false)

const {
  isRecording,
  isUploading,
  isCompleted,
  canStopRecording,
  isDeleting,
  recordingTime,
  uploadProgress,
  currentRecording,
  finalAudioUrl,
  previewStream,
  isPreviewReady,
  previewRotationDeg,
  prepareVideoPreview,
  rotatePreview,
  cancelVideoPreview,
  startRecording: startFeedbackRecording,
  stopRecording: stopFeedbackRecording,
  retakeRecording,
  sendToLine,
  formatTime: formatRecordingTime,
  cleanup: cleanupRecorder,
  cleanupRecordingData,
  deleteFeedbackRecording
} = useFeedbackRecorder()

// 動画録画中のライブプレビュー（自撮り確認用。生カメラstreamをそのまま表示するだけ）
const previewVideoEl = ref(null)
function setPreviewVideoEl(el) {
  previewVideoEl.value = el
  // Vueはこの関数refを再描画のたびに呼び直すため、同じstreamへの再代入による
  // ちらつき（黒く点滅）を防ぐためすでに同じstreamなら何もしない
  if (el && el.srcObject !== (previewStream.value || null)) {
    el.srcObject = previewStream.value || null
  }
}
// 生ストリームは向きの補正をしていないため、プレビュー表示だけCSSで回転させる
// （実際に保存される映像の回転はサーバー側のffmpegが確定角度を使って行う）
const previewVideoStyle = computed(() => {
  const deg = previewRotationDeg.value
  const rotated = deg === 90 || deg === 270
  return {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: rotated ? '133.333%' : '100%',
    height: rotated ? '75%' : '100%',
    objectFit: 'cover',
    transform: `translate(-50%, -50%) rotate(${deg}deg) scaleX(-1)`
  }
})
watch(previewStream, (stream) => {
  if (previewVideoEl.value && previewVideoEl.value.srcObject !== (stream || null)) {
    previewVideoEl.value.srcObject = stream || null
  }
})

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token
}

function formatDateTimeJst (iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', dateStyle: 'short', timeStyle: 'short' })
}

function getStatusText (status) {
  const map = {
    submitted: '提出済み',
    feedback_pending: 'FB待ち',
    feedback_completed: 'FB完了',
    no_reply_needed: '返信不要'
  }
  return map[status] || status || '—'
}

// 返信可能な行のみ（課題で提出済み・FB待ち。練習・FB完了は表示しない）
function canReply (s) {
  return s.type === 'assignment' && (s.status === 'submitted' || s.status === 'feedback_pending')
}

function canDeleteFeedback (s) {
  return s.status === 'feedback_pending' || s.status === 'feedback_completed' || !!s.hasFeedbackAudio
}

// 返信不要にできるのは、まだ返信不要でも完了でもない提出（課題・練習どちらも対象）
function canMarkNoReply (s) {
  return s.status === 'submitted' || s.status === 'feedback_pending'
}

function canUnmarkNoReply (s) {
  return s.status === 'no_reply_needed'
}

const cleanupExpiredLocks = async () => {
  try {
    const { error } = await supabase.rpc('cleanup_expired_feedback_locks')
    if (error) throw error
  } catch (e) {
    console.warn('期限切れロッククリーンアップ:', e)
  }
}

const startLockTimer = () => {
  if (lockTimerInterval.value) {
    clearInterval(lockTimerInterval.value)
    lockTimerInterval.value = null
  }
  const tick = () => {
    if (!lockExpiresAt.value) return
    const now = Date.now()
    const sec = Math.max(0, Math.floor((lockExpiresAt.value - now) / 1000))
    remainingSeconds.value = sec
    remainingTimeDisplay.value = formatRemainingTime(sec)
    if (sec <= 0) {
      if (lockTimerInterval.value) {
        clearInterval(lockTimerInterval.value)
        lockTimerInterval.value = null
      }
      if (showFeedbackModal.value) {
        alert('ロックの期限が切れたため、モーダルを閉じます。')
        closeFeedbackModal().catch((e) => console.error(e))
      }
    }
  }
  tick()
  lockTimerInterval.value = setInterval(tick, 1000)
}

const stopLockTimer = () => {
  if (lockTimerInterval.value) {
    clearInterval(lockTimerInterval.value)
    lockTimerInterval.value = null
  }
  lockExpiresAt.value = null
  remainingTimeDisplay.value = '--:--'
  remainingSeconds.value = 0
}

const openFeedbackModal = async (s, lockResult) => {
  currentFeedbackSubmission.value = s
  submissionAudioUrl.value = null
  submissionAudioLoading.value = true
  if (lockResult?.expires_at) {
    lockExpiresAt.value = new Date(lockResult.expires_at).getTime()
  } else {
    lockExpiresAt.value = Date.now() + 15 * 60 * 1000
  }
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/submissions/${s.id}/submission-audio`, { headers: { Authorization: `Bearer ${token}` } })
    const data = await res.json().catch(() => ({}))
    if (res.ok && data.signedUrl) {
      submissionAudioUrl.value = data.signedUrl
    }
  } catch (e) {
    console.warn('提出音声取得失敗:', e)
  } finally {
    submissionAudioLoading.value = false
  }
  showFeedbackModal.value = true
  startLockTimer()
  await nextTick()
}

const closeFeedbackModal = async (closedAfterSend = false) => {
  if (!canCloseModal.value) return
  const recordingId = currentRecording.value?.recordingId
  if (isRecording.value) {
    await stopFeedbackRecording()
  }
  if (recordingId && !closedAfterSend) {
    try {
      await deleteFeedbackRecording(recordingId)
    } catch (e) {
      console.warn('R2フィードバック削除:', e)
    }
    await cleanupRecordingData(recordingId)
  }
  stopLockTimer()
  const subId = currentFeedbackSubmission.value?.id
  if (subId) {
    try {
      await releaseFeedbackLock(subId, supabase)
    } catch (e) {
      console.error('ロック解除エラー:', e)
      alert('ロックの解除に失敗しました。')
    }
  }
  showFeedbackModal.value = false
  currentFeedbackSubmission.value = null
  submissionAudioUrl.value = null
  if (!isProcessingLock.value) loadSubmissions()
}

const handleReply = async (s) => {
  if (isProcessingLock.value) return
  isProcessingLock.value = true
  try {
    await cleanupExpiredLocks()
    const result = await acceptTaskWithLock({ id: s.id }, supabase, {
      onSuccess: (lockResult) => openFeedbackModal(s, lockResult),
      onConflict: () => {
        alert('この提出は他の講師または管理者が対応中です。')
      }
    })
    if (result?.success) {
      console.log('✅ ロック取得成功（管理者）')
    }
  } catch (e) {
    console.error('返信受付エラー:', e)
    alert(e.message || '返信の受付に失敗しました')
  } finally {
    isProcessingLock.value = false
  }
}

const handleDeleteFeedback = async (s) => {
  if (!s?.id) return
  if (deletingSubmissionId.value) return
  const ok = window.confirm('この提出の先生コメント音声を削除し、再度フィードバック可能な状態に戻します。よろしいですか？')
  if (!ok) return

  deletingSubmissionId.value = s.id
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/submissions/${s.id}/reset-feedback`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.error || 'フィードバック削除に失敗しました')
    }
    alert('フィードバックを削除しました。再度返信できます。')
    await loadSubmissions()
  } catch (e) {
    alert(e.message || 'フィードバック削除に失敗しました')
  } finally {
    deletingSubmissionId.value = null
  }
}

const handleMarkNoReply = async (s) => {
  if (!s?.id || noReplyProcessingId.value) return
  noReplyProcessingId.value = s.id
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/mark-no-reply`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ submission_id: s.id, user_id: s.userId, day: s.day, type: s.type })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.success) {
      throw new Error(data.message || '返信不要マークに失敗しました')
    }
    s.status = 'no_reply_needed'
  } catch (e) {
    alert(e.message || '返信不要マークに失敗しました')
  } finally {
    noReplyProcessingId.value = null
  }
}

const handleUnmarkNoReply = async (s) => {
  if (!s?.id || noReplyProcessingId.value) return
  noReplyProcessingId.value = s.id
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/unmark-no-reply`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ submission_id: s.id, user_id: s.userId, day: s.day, type: s.type })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.success) {
      throw new Error(data.message || '返信不要解除に失敗しました')
    }
    s.status = 'submitted'
  } catch (e) {
    alert(e.message || '返信不要解除に失敗しました')
  } finally {
    noReplyProcessingId.value = null
  }
}

const canShowStartButton = computed(() => !isRecording.value && !isUploading.value && !isCompleted.value && !finalAudioUrl.value)
const canStartNow = computed(() => canShowStartButton.value)
const canCloseModal = computed(() => !isRecording.value && !isUploading.value && !isDeleting.value && !isSubmitting.value)
const lockTimerClass = computed(() => {
  const sec = remainingSeconds.value
  if (sec <= 60) return 'timer-danger'
  if (sec <= 180) return 'timer-warning'
  return 'timer-normal'
})
const showExpiryWarning = computed(() => {
  const sec = remainingSeconds.value
  return sec > 0 && sec <= 60
})

// フィードバック録音モード（音声/動画）。動画はAndroid/PCのみ対応
const isVideoSupported = isVideoFeedbackSupported()
const selectedMediaKind = ref(isVideoSupported ? 'video' : 'audio')

const startRecording = async () => {
  if (!canStartNow.value) return
  const s = currentFeedbackSubmission.value
  if (!s) return
  const submissionData = { id: s.id, user_id: s.userId, day: s.day, type: s.type || 'assignment' }
  await startFeedbackRecording(submissionData, selectedMediaKind.value)
}

const isPreparingPreview = ref(false)
// 「カメラ準備」ボタン: 録画開始前にカメラプレビューを準備する
async function onPrepareVideoPreview() {
  const s = currentFeedbackSubmission.value
  if (!s) return
  const submissionData = { id: s.id, user_id: s.userId, day: s.day, type: s.type || 'assignment' }
  isPreparingPreview.value = true
  try {
    await prepareVideoPreview(submissionData)
  } catch {
    // エラー内容はprepareVideoPreview内でalert済み
  } finally {
    isPreparingPreview.value = false
  }
}
// 「向きを直す」ボタン: プレビューを90度ずつ回転（保存も内部で行う）
function onRotatePreview() {
  rotatePreview()
}
// プレビュー確認後の「録画開始」ボタン
async function onStartVideoRecording() {
  await startRecording()
}
// プレビューの「キャンセル」ボタン
function onCancelVideoPreview() {
  cancelVideoPreview()
}

const stopRecording = async () => {
  try {
    await stopFeedbackRecording()
  } catch (error) {
    console.error('フィードバック録音停止エラー:', error)
    alert(`録音停止エラー: ${error.message}`)
  }
}

const handleSendFeedback = async () => {
  if (isSubmitting.value) return
  isSubmitting.value = true
  try {
    await sendToLine()
    alert('フィードバックを送信しました。')
    await closeFeedbackModal(true)
  } catch (e) {
    console.error('送信エラー:', e)
    alert(e.message || '送信に失敗しました')
  } finally {
    isSubmitting.value = false
  }
}

let lastVisibilityHandledAt = 0
const VISIBILITY_DEBOUNCE_MS = 2000
const handleVisibilityChange = async () => {
  if (document.hidden) return
  if (showFeedbackModal.value && lockExpiresAt.value != null && Date.now() >= lockExpiresAt.value) {
    const now = Date.now()
    if (now - lastVisibilityHandledAt < VISIBILITY_DEBOUNCE_MS) return
    lastVisibilityHandledAt = now
    alert('ロックの期限が切れたため、モーダルを閉じます。')
    await closeFeedbackModal()
  }
}

function buildQueryParams (offset = 0) {
  const params = new URLSearchParams()
  if (filterType.value !== 'all') params.set('type', filterType.value)
  if (filterStatus.value !== 'all') params.set('status', filterStatus.value)
  if (filterDateFrom.value) params.set('dateFrom', filterDateFrom.value + 'T00:00:00.000Z')
  if (filterDateTo.value) params.set('dateTo', filterDateTo.value + 'T23:59:59.999Z')
  params.set('limit', String(PAGE_SIZE))
  params.set('offset', String(offset))
  return params.toString()
}

async function downloadCsv (url, defaultFilename) {
  const token = await getToken()
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'ダウンロードに失敗しました')
  }
  const disposition = res.headers.get('Content-Disposition')
  const filename = disposition ? (disposition.match(/filename="([^"]+)"/)?.[1] || defaultFilename) : defaultFilename
  const blob = await res.blob()
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}

const downloadSubmissionsCsv = async () => {
  exportCsvLoading.value = true
  try {
    const params = new URLSearchParams()
    if (filterDateFrom.value) params.set('dateFrom', filterDateFrom.value + 'T00:00:00.000Z')
    if (filterDateTo.value) params.set('dateTo', filterDateTo.value)
    if (filterType.value !== 'all') params.set('type', filterType.value)
    if (filterStatus.value !== 'all') params.set('status', filterStatus.value)
    const url = `${apiBase}/api/manager/export/submissions?${params.toString()}`
    await downloadCsv(url, 'submissions.csv')
  } catch (e) {
    alert(e.message || 'CSVの取得に失敗しました')
  } finally {
    exportCsvLoading.value = false
  }
}

const loadSubmissions = async () => {
  loading.value = true
  currentOffset.value = 0
  try {
    const token = await getToken()
    const qs = buildQueryParams(0)
    const res = await fetch(`${apiBase}/api/manager/submissions?${qs}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      submissions.value = []
      total.value = 0
      return
    }
    submissions.value = data.submissions || []
    total.value = data.total ?? 0
  } catch (_) {
    submissions.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

const loadMore = async () => {
  const nextOffset = currentOffset.value + PAGE_SIZE
  if (nextOffset >= total.value) return
  loadingMore.value = true
  try {
    const token = await getToken()
    const qs = buildQueryParams(nextOffset)
    const res = await fetch(`${apiBase}/api/manager/submissions?${qs}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) return
    const list = data.submissions || []
    submissions.value = [...submissions.value, ...list]
    currentOffset.value = nextOffset + list.length
  } finally {
    loadingMore.value = false
  }
}

const playSubmissionAudio = async (submissionId) => {
  if (!audioEl.value) return
  playingSubmissionAudioId.value = submissionId
  playingId.value = null
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/submissions/${submissionId}/submission-audio`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.signedUrl) {
      playingSubmissionAudioId.value = null
      alert(data.error || '提出音声の取得に失敗しました')
      return
    }
    if (feedbackVideoEl.value) {
      feedbackVideoEl.value.pause()
      feedbackVideoEl.value.removeAttribute('src')
    }
    playingMediaKind.value = 'audio'
    audioEl.value.src = data.signedUrl
    audioEl.value.play().catch(() => {
      playingSubmissionAudioId.value = null
    })
  } catch (e) {
    playingSubmissionAudioId.value = null
    alert(e.message || '再生に失敗しました')
  }
}

function onAudioEnded () {
  playingId.value = null
  playingSubmissionAudioId.value = null
}
function onAudioPause () {
  playingId.value = null
  playingSubmissionAudioId.value = null
}
function onAudioError () {
  playingId.value = null
  playingSubmissionAudioId.value = null
}

const playFeedback = async (submissionId) => {
  if (!audioEl.value) return
  playingId.value = submissionId
  playingSubmissionAudioId.value = null
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/submissions/${submissionId}/feedback-audio`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.signedUrl) {
      playingId.value = null
      alert(data.error || '音声の取得に失敗しました')
      return
    }
    if (data.mimeType && data.mimeType.startsWith('video/')) {
      audioEl.value.pause()
      audioEl.value.removeAttribute('src')
      playingMediaKind.value = 'video'
      if (feedbackVideoEl.value) {
        feedbackVideoEl.value.src = data.signedUrl
        feedbackVideoEl.value.play().catch(() => {
          playingId.value = null
        })
      }
    } else {
      if (feedbackVideoEl.value) {
        feedbackVideoEl.value.pause()
        feedbackVideoEl.value.removeAttribute('src')
      }
      playingMediaKind.value = 'audio'
      audioEl.value.src = data.signedUrl
      audioEl.value.play().catch(() => {
        playingId.value = null
      })
    }
  } catch (e) {
    playingId.value = null
    alert(e.message || '再生に失敗しました')
  }
}

onMounted(() => {
  loadSubmissions()
  loadDay0Submissions()
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (lockTimerInterval.value) {
    clearInterval(lockTimerInterval.value)
    lockTimerInterval.value = null
  }
  cleanupRecorder()
  stopDay0RecordingTimer()
  day0FeedbackRecorder.cancelPreview()
})
</script>

<style scoped>
.manager-submissions {
  padding: 0;
}

.page-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  color: #1e293b;
}

.page-desc {
  margin: 0 0 1.5rem 0;
  color: #64748b;
  font-size: 0.9rem;
}

.manager-section {
  margin-bottom: 2rem;
}

.section-title {
  margin: 0 0 0.75rem 0;
  font-size: 1.1rem;
  color: #334155;
}

.card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-label {
  font-size: 0.8rem;
  color: #64748b;
}

.form-select,
.form-input {
  padding: 0.4rem 0.6rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.total-note {
  font-size: 0.9rem;
  color: #64748b;
}

.loading-state,
.empty-state {
  padding: 2rem;
  text-align: center;
  color: #64748b;
}

.table-wrap {
  overflow-x: auto;
}

.submissions-table-wrap {
  max-height: 70vh;
  overflow-y: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.data-table th,
.data-table td {
  padding: 0.5rem 0.6rem;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
}

.data-table th.num,
.data-table td.num {
  text-align: right;
}

.data-table thead th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 1;
}

.data-row:hover {
  background: #f8fafc;
}

.type-badge,
.status-badge {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
}

.type-badge.assignment {
  background: #dbeafe;
  color: #1e40af;
}

.type-badge.practice {
  background: #dcfce7;
  color: #166534;
}

.status-badge.submitted {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.feedback_pending {
  background: #e0e7ff;
  color: #3730a3;
}

.status-badge.feedback_completed {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.no_reply_needed {
  background: #e5e7eb;
  color: #4b5563;
}

.btn-play {
  font-size: 0.8rem;
}

.no-audio {
  color: #94a3b8;
}

.load-more {
  margin-top: 1rem;
  text-align: center;
}

.feedback-audio-player {
  margin-top: 1rem;
  width: 100%;
  max-width: 400px;
}

.feedback-video-review-player {
  margin-top: 1rem;
  width: 100%;
  max-width: 280px;
  aspect-ratio: 3 / 4;
  display: block;
  background: #000;
  border-radius: 8px;
}

.data-table th.action,
.data-table td.action {
  white-space: nowrap;
}
.btn-reply {
  font-size: 0.8rem;
}
.btn-delete {
  font-size: 0.8rem;
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
}
.btn-no-reply {
  font-size: 0.8rem;
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}
.btn-unmark-no-reply {
  font-size: 0.8rem;
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}
.action-buttons {
  display: flex;
  gap: 0.4rem;
}
.no-action {
  color: #94a3b8;
}

/* 管理者用フィードバックモーダル（講師homeと同じ見た目） */
.fullscreen-feedback-modal {
  position: fixed;
  inset: 0;
  background: white;
  z-index: 2000;
  overflow: hidden;
}
.fullscreen-modal-enter-active { transition: all 0.3s ease-out; }
.fullscreen-modal-leave-active { transition: all 0.2s ease-in; }
.fullscreen-modal-enter-from { transform: scale(0.8); opacity: 0; }
.fullscreen-modal-leave-to { transform: scale(0.9); opacity: 0; }
.fullscreen-content {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}
.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  cursor: pointer;
  z-index: 3000;
}
.close-btn:disabled { background: #9ca3af; cursor: not-allowed; opacity: 0.6; }
.lock-timer-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  margin: 0 1rem;
  border-radius: 8px;
  font-weight: 600;
  flex-shrink: 0;
}
.lock-timer-bar.timer-normal { background: #dcfce7; color: #166534; }
.lock-timer-bar.timer-warning { background: #fef3c7; color: #92400e; }
.lock-timer-bar.timer-danger { background: #fee2e2; color: #991b1b; }
.lock-timer-value { font-variant-numeric: tabular-nums; }
.lock-timer-warning { font-size: 0.8125rem; margin-left: auto; }
.feedback-screen {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 1rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}
.feedback-header-mobile {
  padding: 0.5rem 72px 1rem 0.75rem;
  margin-bottom: 1rem;
  border-bottom: 2px solid #f3f4f6;
}
.student-name-line { font-size: 1.3rem; font-weight: bold; color: #1f2937; }
.assignment-line { font-size: 1.1rem; font-weight: bold; color: #3b82f6; }
.feedback-sections { display: flex; flex-direction: column; gap: 1.5rem; }
.manual-title { margin: 0 0 0.5rem 0; font-size: 1rem; }
.manual-content .manual-steps { margin: 0; }
.manual-content .manual-steps p { margin: 0.5rem 0; font-size: 0.9rem; color: #475569; }
.feedback-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
}
.feedback-controls-section { margin-top: 0.5rem; }
.submitted-audio-title { margin: 0 0 0.5rem 0; font-size: 1rem; }
.submitted-audio-section { margin-top: 0.5rem; }
.audio-slot { padding: 0.5rem 0; color: #64748b; }
.audio-player-section { margin-top: 0.5rem; }
.fullscreen-audio-player { width: 100%; max-width: 400px; }
.recording-start { margin-top: 1rem; }
.media-kind-toggle { display: flex; justify-content: center; gap: 1rem; margin-bottom: 0.75rem; flex-wrap: wrap; }
.media-kind-toggle label { display: flex; align-items: center; gap: 0.3rem; cursor: pointer; }
.media-kind-toggle label.is-disabled { opacity: 0.45; cursor: not-allowed; }
.media-kind-note { width: 100%; font-size: 0.8rem; color: #888; margin: 0.25rem 0 0; }
/* 撮影後のチェック。切り抜く前の手元の録画を、届く動画と同じく3:4の中央で切って見せる（データ自体は変えない） */
.feedback-video-player { width: 100%; max-width: 280px; aspect-ratio: 3 / 4; object-fit: cover; margin: 0 auto; display: block; border-radius: 8px; background: #000; }
.preview-outer { position: relative; width: 100%; max-width: 280px; aspect-ratio: 3 / 4; margin: 0 auto 0.75rem; overflow: hidden; border-radius: 8px; background: #000; }
.recording-preview { display: block; }
.video-preview-section { text-align: center; }
.rotate-btn { display: block; margin: 0 auto 0.75rem; }
.preview-actions { display: flex; gap: 0.5rem; justify-content: center; }
.recording-active {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.recording-indicator { display: flex; align-items: center; gap: 0.5rem; }
.recording-dot {
  width: 10px;
  height: 10px;
  background: #ef4444;
  border-radius: 50%;
  animation: pulse 1s infinite;
}
@keyframes pulse { 50% { opacity: 0.5; } }
.upload-progress-section { margin-top: 1rem; }
.upload-indicator { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; }
.progress-bar { height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
.progress-fill { height: 100%; background: #3b82f6; transition: width 0.2s; }
.progress-text { font-size: 0.85rem; color: #64748b; margin-top: 0.25rem; }
.recording-completed { margin-top: 1rem; }
.completed-message { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem; }
.success-icon { font-size: 1.25rem; }
.recording-actions { display: flex; gap: 1rem; margin-top: 1rem; }
.btn.large { padding: 0.75rem 1.5rem; font-size: 1rem; }

.day0-inline-audio {
  max-width: 220px;
  height: 32px;
}

.day0-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.day0-modal-content {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  max-width: 480px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.day0-modal-audio {
  width: 100%;
}

/* 撮影中の表示・撮影後のチェックとも、サーバーの変換（中央を縦長3:4に切り抜く）と同じ範囲を見せる。
   カメラの映像・手元の録画は切り抜く前なので、3:4の枠からはみ出る部分を中央で切って表示する（データ自体は変えない） */
.day0-modal-video {
  display: block;
  width: 100%;
  max-width: 280px;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  margin: 0 auto;
  background: #000;
  border-radius: 8px;
}

/* 自撮りのライブプレビューだけ鏡のように左右反転する（実際に保存される映像データ自体は反転しない） */
.day0-modal-video-live {
  transform: scaleX(-1);
}

.day0-mode-toggle {
  display: flex;
  gap: 1rem;
  font-size: 0.9rem;
}

.day0-preview-note {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
}

.day0-recording-timer {
  font-size: 1.1rem;
  font-weight: bold;
  color: #dc2626;
  margin: 0;
}

.day0-modal-actions {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.day0-modal-error {
  color: #dc2626;
  font-size: 0.85rem;
  margin: 0;
}

.day0-modal-close {
  align-self: flex-end;
}

.day0-voice-badge {
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  background: #ede9fe;
  color: #5b21b6;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.day0-duration {
  display: block;
  color: #64748b;
  font-size: 0.75rem;
}
</style>
